import React from 'react';

// Lightweight markdown renderer for training-lesson bodies.
// Supports: # / ## / ### headings, **bold**, *italic*, `inline code`,
// ``` fenced code blocks ```, - bullet lists, GFM-style pipe tables,
// blank-line paragraphs. No external dependency.

function renderInline(text) {
  // Replace inline code first to protect content from other patterns.
  const parts = [];
  let i = 0;
  let key = 0;
  while (i < text.length) {
    const tickIdx = text.indexOf('`', i);
    if (tickIdx === -1) {
      parts.push(formatBoldItalic(text.slice(i), key++));
      break;
    }
    if (tickIdx > i) parts.push(formatBoldItalic(text.slice(i, tickIdx), key++));
    const close = text.indexOf('`', tickIdx + 1);
    if (close === -1) {
      parts.push(formatBoldItalic(text.slice(tickIdx), key++));
      break;
    }
    parts.push(
      <code
        key={`code-${key++}`}
        style={{
          background: '#f1f5f9',
          padding: '1px 6px',
          borderRadius: 4,
          fontFamily: 'Consolas, Menlo, monospace',
          fontSize: '0.92em'
        }}
      >
        {text.slice(tickIdx + 1, close)}
      </code>
    );
    i = close + 1;
  }
  return parts;
}

function formatBoldItalic(text, baseKey) {
  // Process **bold** and *italic*.
  const out = [];
  let i = 0;
  let k = 0;
  while (i < text.length) {
    if (text[i] === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        out.push(<strong key={`b-${baseKey}-${k++}`}>{text.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    if (text[i] === '*') {
      const end = text.indexOf('*', i + 1);
      if (end !== -1) {
        out.push(<em key={`i-${baseKey}-${k++}`}>{text.slice(i + 1, end)}</em>);
        i = end + 1;
        continue;
      }
    }
    // Plain text run up to the next * or end
    let j = i;
    while (j < text.length && text[j] !== '*') j++;
    out.push(<span key={`t-${baseKey}-${k++}`}>{text.slice(i, j)}</span>);
    i = j;
  }
  return <React.Fragment key={`f-${baseKey}`}>{out}</React.Fragment>;
}

function renderTable(rows, key) {
  // rows is an array of "| a | b | c |" lines
  // The 2nd row is alignment dashes; skip it.
  const cells = rows.map(r =>
    r.replace(/^\||\|$/g, '').split('|').map(c => c.trim())
  );
  if (cells.length < 2) return null;
  const header = cells[0];
  const body = cells.slice(2);
  return (
    <div key={key} style={{ overflowX: 'auto', margin: '12px 0' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>
        <thead>
          <tr>
            {header.map((h, i) => (
              <th
                key={i}
                style={{
                  border: '1px solid #cbd5e1',
                  padding: '8px 12px',
                  background: '#f1f5f9',
                  textAlign: 'left'
                }}
              >
                {renderInline(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, r) => (
            <tr key={r}>
              {row.map((c, i) => (
                <td
                  key={i}
                  style={{
                    border: '1px solid #cbd5e1',
                    padding: '8px 12px',
                    verticalAlign: 'top'
                  }}
                >
                  {renderInline(c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function LessonMarkdown({ source }) {
  if (!source) return null;
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.startsWith('```')) {
      const buf = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        buf.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++; // skip closing ```
      out.push(
        <pre
          key={key++}
          style={{
            background: '#0f172a',
            color: '#e2e8f0',
            padding: '12px 16px',
            borderRadius: 6,
            overflowX: 'auto',
            fontSize: 13,
            fontFamily: 'Consolas, Menlo, monospace',
            margin: '12px 0'
          }}
        >
          {buf.join('\n')}
        </pre>
      );
      continue;
    }

    // Headings
    if (line.startsWith('### ')) {
      out.push(
        <h3 key={key++} style={{ marginTop: 22, marginBottom: 6, color: '#1e293b' }}>
          {renderInline(line.slice(4))}
        </h3>
      );
      i++;
      continue;
    }
    if (line.startsWith('## ')) {
      out.push(
        <h2 key={key++} style={{ marginTop: 28, marginBottom: 8, color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>
          {renderInline(line.slice(3))}
        </h2>
      );
      i++;
      continue;
    }
    if (line.startsWith('# ')) {
      out.push(
        <h1 key={key++} style={{ marginTop: 8, marginBottom: 12, color: '#0f172a' }}>
          {renderInline(line.slice(2))}
        </h1>
      );
      i++;
      continue;
    }

    // Table (consecutive lines starting with `|`)
    if (line.startsWith('|') && i + 1 < lines.length && lines[i + 1].startsWith('|')) {
      const buf = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        buf.push(lines[i]);
        i++;
      }
      const rendered = renderTable(buf, key++);
      if (rendered) out.push(rendered);
      continue;
    }

    // Bulleted list
    if (line.match(/^[-*]\s+/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^[-*]\s+/)) {
        items.push(lines[i].replace(/^[-*]\s+/, ''));
        i++;
      }
      out.push(
        <ul key={key++} style={{ margin: '8px 0 12px 0', paddingLeft: 22, lineHeight: 1.55 }}>
          {items.map((it, idx) => (
            <li key={idx} style={{ marginBottom: 4 }}>{renderInline(it)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (line.match(/^\d+\.\s+/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\d+\.\s+/)) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      out.push(
        <ol key={key++} style={{ margin: '8px 0 12px 0', paddingLeft: 22, lineHeight: 1.55 }}>
          {items.map((it, idx) => (
            <li key={idx} style={{ marginBottom: 4 }}>{renderInline(it)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Blank line — paragraph break
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Paragraph: gather consecutive non-blank, non-special lines
    const buf = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('#') &&
      !lines[i].startsWith('```') &&
      !lines[i].startsWith('|') &&
      !lines[i].match(/^[-*]\s+/) &&
      !lines[i].match(/^\d+\.\s+/)
    ) {
      buf.push(lines[i]);
      i++;
    }
    out.push(
      <p key={key++} style={{ margin: '10px 0', lineHeight: 1.6, color: '#1f2937' }}>
        {renderInline(buf.join(' '))}
      </p>
    );
  }

  return <div className="lesson-markdown">{out}</div>;
}
